import React, { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { BookOpen, Check, X, Lock, LogOut, SlidersHorizontal } from "lucide-react";
import { useTheme } from "../../context/ThemeProvider";
import { LogoutSvg } from "../../assets";
import { logout } from "../../api/logout";
import { useNavigate, useLocation } from "react-router-dom";
import ROUTES from "../../router/routes";
import UIStore from "../../store/uiStore";
import UserAuthStore from "../../store/userStore";
import api from "../../api";
import ENDPOINTS from "../../api/endpoints";
import useFeedback from "../../hooks/useFeedback";

const SidebarDrawerBottomSection = () => {
  const { showFeedback } = useFeedback();
  useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isSettingsActive = location.pathname.startsWith(ROUTES.SETTINGS.ROOT) && location.pathname !== ROUTES.SETTINGS.SCHEDULED_JOBS;
  const setShowSidebar = UIStore((state) => state.setShowSidebar);
  const firebaseUser = UserAuthStore((state) => state.user);
  const isPasswordUser = firebaseUser?.providerData?.[0]?.providerId === "password";
  const [pendingPasswordReset, setPendingPasswordReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleConfirmChangePassword = async () => {
    if (!firebaseUser?.email) return;
    setPendingPasswordReset(false);
    try {
      await api({ method: "POST", url: ENDPOINTS.PASSWORD_RESET });
      setResetSent(true);
      setTimeout(() => setResetSent(false), 5000);
    } catch {
      showFeedback("Failed to send reset email. Please try again.", "error");
    }
  };

  const handleNavigateSettings = () => {
    navigate(ROUTES.SETTINGS.ROOT);
    if (window.innerWidth < 940) setShowSidebar(false);
  };

  return (
    <div className="px-3 py-[6px] bg-surface-sidebar border-t border-edge-gray-200 dark:border-edge-panel-border grid grid-cols-3 gap-1">

      {/* Settings — navigates to landing page */}
      <button
        onClick={handleNavigateSettings}
        aria-current={isSettingsActive ? "page" : undefined}
        className="photon-rail-control h-14 min-w-0 flex flex-col items-center justify-center gap-0.5 rounded-lg transition-colors duration-150"
      >
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          <SlidersHorizontal className="w-5 h-5" aria-hidden="true" />
        </div>
        <span className="font-medium text-[11px] leading-4">Settings</span>
      </button>

      <a
        href="/docs"
        target="_blank"
        rel="noopener noreferrer"
        className="photon-rail-control h-14 min-w-0 flex flex-col items-center justify-center gap-0.5 rounded-lg transition-colors duration-150"
        aria-label="Documentation"
        title="Documentation"
      >
        <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-5 h-5" aria-hidden="true" />
        </span>
        <span className="font-medium text-[11px] leading-4">Docs</span>
      </a>

      {/* User actions — logout + optional password reset */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className="photon-rail-control h-14 min-w-0 flex flex-col items-center justify-center gap-0.5 rounded-lg transition-colors duration-150"
            aria-label="Account actions"
            title="Account"
          >
            <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <LogOut className="w-5 h-5" aria-hidden="true" />
            </span>
            <span className="font-medium text-[11px] leading-4">Logout</span>
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content className="absolute right-0 bottom-12 w-44 bg-fill-white dark:bg-fill-dialog-surface border border-edge-gray-200 dark:border-edge-panel-border rounded-xl shadow-xl z-[101] py-1">
          {isPasswordUser && (
            <>
              <DropdownMenu.Item asChild onSelect={(e) => e.preventDefault()}>
                <div className="flex items-center justify-between w-full px-4 py-2.5 text-[12px] text-ink-ink dark:text-ink-gray-200 rounded-lg">
                  {pendingPasswordReset ? (
                    <>
                      <span className="text-ink-gray-500 dark:text-ink-gray-400">Send reset email?</span>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          type="button"
                          onClick={handleConfirmChangePassword}
                          title="Yes, send it"
                          className="w-6 h-6 rounded-full flex items-center justify-center bg-fill-green-100 dark:bg-fill-green-600/20 text-ink-green-600 dark:text-ink-green-300 hover:opacity-80 transition-opacity"
                        >
                          <Check className="w-[11px] h-[11px]" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingPasswordReset(false)}
                          title="Cancel"
                          className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 text-ink-gray-400 hover:text-ink-red-600 dark:hover:text-ink-red-300 transition-colors"
                        >
                          <X className="w-[11px] h-[11px]" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="flex items-center justify-between w-full focus:outline-none disabled:opacity-50"
                      onClick={() => !resetSent && setPendingPasswordReset(true)}
                      disabled={resetSent}
                    >
                      <span>{resetSent ? "Reset link sent" : "Change Password"}</span>
                      <Lock className="w-[14px] h-[14px] ml-2 opacity-60" />
                    </button>
                  )}
                </div>
              </DropdownMenu.Item>
              <div className="my-1 h-px bg-fill-gray-200 dark:bg-fill-panel-border" />
            </>
          )}
          <DropdownMenu.Item asChild>
            <button
              className="flex items-center justify-between w-full px-4 py-2.5 text-[12px] text-ink-ink dark:text-ink-gray-200 transition-colors rounded-lg focus:outline-none"
              onClick={logout}
              type="button"
            >
              <span>Logout</span>
              <LogoutSvg className="w-4 h-4 ml-2" />
            </button>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>

    </div>
  );
};

export default SidebarDrawerBottomSection;

